<template>
  <div :class="style.content">
    <div :class="style.tabs">
      <TabPanel
        text="Настройки"
        :active="activeSection === 'Настройки'"
        @click="setActiveSection('Настройки')"
      />

      <TabPanel
        text="Контент"
        :active="activeSection === 'Контент'"
        @click="setActiveSection('Контент')"
      />
    </div>

    <div v-if="activeSection === 'Настройки'">
      <div :class="style.block" v-if="mobileVersion">
        <SelectBase
          comment="Тип блока"
          :text="logoTypeText"
          :items="logoTypeOptions"
          @select="setLogoType"
        />
      </div>

      <div :class="style.block" v-if="isShowLogoSlider">
        <InputSlider
          v-model="logoRangeSize"
          text="Размер"
          size="M"
          suffix="Px"
          :min="50"
          :max="150"
        />
      </div>
    </div>

    <div v-if="activeSection === 'Контент'">
      <div :class="style.fileUploadBlock">
        <InputFieldMedia
          :description="descriptionLogo"
          :isFile="isLogoInputFile"
          :text="logoInputStyle"
          :modelValue="logoNameText"
          :placeholder="logoInputPlaceholder"
          :image="logoImage"
          :hasImage="logoInputHasImage"
          :actions="logoActions"
          @upload="uploadLogo"
          @showInfo="openLogoActionMenu"
          @update:modelValue="setLogoNameText"
        />

        <input
          ref="loadFileInput"
          type="file"
          accept="image/*"
          hidden
          @change="setImg($event, 'logo')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, markRaw} from "vue";

import style from "./LogoSettingComponent.module.scss";
import {DOMAIN} from "@/http";

import TabPanel from "@/elements/UI/tabs/TabPanel/TabPanel.vue";
import InputSlider from "@/elements/UI/input/Slider/InputSlider.vue";
import InputFieldMedia from "@/elements/UI/input/Media/InputFieldMedia.vue";
import ActionMenu from "@/elements/UI/popup/ActionMenu/ActionMenu.vue";
import SelectBase from "@/elements/UI/select/SelectBase.vue";

import deleteIcon from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";
import uploadIcon from "$images/icons/button/Name=Upload, Size=16, Tone=Default.svg";

import {modifyQuiz} from "@/page/quiz/scriptQuiz";
import {LogoType} from "@/page/quiz/typeQuiz";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";

export default defineComponent({
  name: "LogoSettingComponent",

  components: {
    SelectBase,
    ActionMenu,
    InputFieldMedia,
    InputSlider,
    TabPanel
  },

  data() {
    return {
      style,
      modifyQuiz,

      activeSection: "Настройки" as SelectedPanel,

      newLogo: "",
      cachedPreviewUrls: {} as Record<string, string>,
      isLogoActionMenuOpen: false,

      deleteIcon,
      uploadIcon
    };
  },

  mounted() {
    this.setDefaultLogoType();
  },

  beforeUnmount() {
    this.revokePreviewUrls();
  },

  methods: {
    openLogoActionMenu(): void {
      this.isLogoActionMenuOpen = true;
    },

    closeLogoActionMenu(): void {
      this.isLogoActionMenuOpen = false;
    },

    getCurrentLogoType(): LogoType {
      const currentType = this.modifyQuiz("get", "", ["styleName", "type"]) as
        LogoType | undefined;

      if (
        currentType === "onlyLogo" ||
        currentType === "onlyName" ||
        currentType === "both"
      ) {
        return currentType;
      }

      return this.mobileVersion ? "onlyName" : "both";
    },

    setDefaultLogoType(): void {
      if (!this.quiz) {
        return;
      }

      const currentType = this.modifyQuiz("get", "", ["styleName", "type"]) as
        LogoType | undefined;

      if (
        currentType === "onlyLogo" ||
        currentType === "onlyName" ||
        currentType === "both"
      ) {
        return;
      }

      const defaultType: LogoType = this.mobileVersion ? "onlyName" : "both";

      this.modifyQuiz("set", defaultType, ["styleName", "type"]);
    },

    setLogoType(payload: SelectBasePayload): void {
      if (!this.quiz) {
        return;
      }

      const value = payload.value;

      if (value !== "onlyLogo" && value !== "onlyName" && value !== "both") {
        return;
      }

      this.modifyQuiz("set", value, ["styleName", "type"]);
    },

    setLogoNameText(value: string): void {
      if (!this.quiz) {
        return;
      }

      this.modifyQuiz("set", value, ["companyName"]);
    },

    deleteLogo(): void {
      if (!this.quiz) {
        return;
      }

      if (this.newLogo.startsWith("blob:")) {
        URL.revokeObjectURL(this.newLogo);
      }

      const logoName = this.currentLogo;

      if (logoName && this.cachedPreviewUrls[logoName]?.startsWith("blob:")) {
        URL.revokeObjectURL(this.cachedPreviewUrls[logoName]);
      }

      if (logoName) {
        delete this.cachedPreviewUrls[logoName];

        const cachedFileIndex = this.cachedFiles.findIndex((file: File) => {
          return file.name === logoName;
        });

        if (cachedFileIndex !== -1) {
          this.cachedFiles.splice(cachedFileIndex, 1);
        }
      }

      this.newLogo = "";

      this.modifyQuiz("set", "", ["logo"]);

      this.closeLogoActionMenu();
    },

    uploadLogoFromMenu(): void {
      this.closeLogoActionMenu();
      this.uploadLogo();
    },

    getCachedFilePreviewUrl(fileName: string): string {
      const baseName = this.getFileBaseName(fileName);
      const cachedFile = this.cachedFiles.find((file: File) => {
        return file.name === fileName || file.name === baseName;
      });

      if (!cachedFile) {
        return "";
      }

      const currentPreviewUrl = this.cachedPreviewUrls[fileName];

      if (currentPreviewUrl) {
        return currentPreviewUrl;
      }

      const previewUrl = URL.createObjectURL(cachedFile);

      this.cachedPreviewUrls = {
        ...this.cachedPreviewUrls,
        [fileName]: previewUrl
      };

      return previewUrl;
    },

    getFileBaseName(path: string): string {
      return (
        String(path || "")
          .split("/")
          .pop() || ""
      );
    },

    getServerLogoUrl(path: string): string {
      if (!path) {
        return "";
      }

      if (path.startsWith("blob:")) {
        return path;
      }

      if (path.startsWith("http://") || path.startsWith("https://")) {
        return path;
      }

      const params = new URLSearchParams({name: path});

      return `https://${DOMAIN}/api/img?${params.toString()}`;
    },

    revokePreviewUrls(): void {
      if (this.newLogo.startsWith("blob:")) {
        URL.revokeObjectURL(this.newLogo);
      }

      Object.values(this.cachedPreviewUrls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      this.cachedPreviewUrls = {};
    },

    setActiveSection(name: SelectedPanel): void {
      this.activeSection = name;
    },

    uploadLogo(): void {
      const input = this.$refs.loadFileInput as HTMLInputElement | undefined;

      input?.click();
    },

    async setImg(e: Event, name: string): Promise<void> {
      const target = e.target as HTMLInputElement;

      if (!target.files?.length || !this.quiz) {
        target.value = "";
        return;
      }

      const file = target.files[0];
      const blob = file.slice(0, file.size, file.type);
      const nameSplit = file.name.split(".");
      const type = nameSplit[nameSplit.length - 1];
      const newName = `${name}${this.quiz._id}.${type}`;

      const newFile = new File([blob], newName, {
        type: file.type
      });

      const srcFile = URL.createObjectURL(newFile);

      if (this.newLogo.startsWith("blob:")) {
        URL.revokeObjectURL(this.newLogo);
      }

      const oldLogo = this.modifyQuiz("get", "", ["logo"]) as
        string | undefined;

      if (oldLogo) {
        const oldLogoBaseName = this.getFileBaseName(oldLogo);
        const oldCachedFileIndex = this.cachedFiles.findIndex(
          (cachedFile: File) => {
            return (
              cachedFile.name === oldLogo || cachedFile.name === oldLogoBaseName
            );
          }
        );

        if (oldCachedFileIndex !== -1) {
          this.cachedFiles.splice(oldCachedFileIndex, 1);
        }

        const oldPreviewUrl = this.cachedPreviewUrls[oldLogo];

        if (oldPreviewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(oldPreviewUrl);
        }

        delete this.cachedPreviewUrls[oldLogo];
      }

      const sameFileIndex = this.cachedFiles.findIndex((cachedFile: File) => {
        return cachedFile.name === newName;
      });

      if (sameFileIndex !== -1) {
        this.cachedFiles.splice(sameFileIndex, 1);
      }

      this.newLogo = srcFile;

      this.modifyQuiz("set", newName, ["logo"]);

      this.cachedFiles.push(newFile);

      target.value = "";
    }
  },

  computed: {
    quiz() {
      return this.$quizStore.getQuiz;
    },

    selectedQuestion() {
      return this.$quizStore.getSelectedQuestion;
    },

    cachedFiles() {
      return this.$quizStore.getCachedFiles;
    },

    mobileVersion(): boolean {
      return this.$quizStore.getSelectedVersion === "Мобильная";
    },

    currentLogoType(): LogoType {
      return this.getCurrentLogoType();
    },

    isOnlyLogoStyleLogo(): boolean {
      return this.currentLogoType === "onlyLogo";
    },

    isBothStyleLogo(): boolean {
      return this.currentLogoType === "both";
    },

    isOnlyNameStyleLogo(): boolean {
      return this.currentLogoType === "onlyName" && this.mobileVersion;
    },

    logoText(): string {
      return this.currentLogo || "Файл";
    },

    logoTypeText(): string {
      const currentOption = this.logoTypeOptions.find((option) => {
        return option.value === this.currentLogoType;
      });

      return currentOption?.text || "Только название";
    },

    logoNameText(): string {
      const value = this.modifyQuiz("get", "", ["companyName"]) as
        string | undefined;

      return typeof value === "string" ? value : "";
    },

    logoInputStyle(): string {
      if (this.isOnlyLogoStyleLogo || !this.mobileVersion) {
        return this.logoText;
      }

      return "";
    },

    logoInputPlaceholder(): string {
      if (this.isBothStyleLogo) {
        return "Введите название";
      }

      if (this.isOnlyNameStyleLogo) {
        return "Введите название";
      }

      return "";
    },

    logoInputHasImage(): boolean {
      return !this.isOnlyNameStyleLogo;
    },

    isLogoInputFile(): boolean {
      return this.isOnlyLogoStyleLogo || !this.mobileVersion;
    },

    descriptionLogo(): string {
      if (!this.mobileVersion) {
        return "Файл";
      }

      return this.isBothStyleLogo || this.isOnlyNameStyleLogo
        ? "Название"
        : "Файл";
    },

    logoRangeSize: {
      get(): number {
        const value = Number(this.quiz?.rangeSize);

        if (!Number.isFinite(value)) {
          return 100;
        }

        return value;
      },

      set(value: number): void {
        if (!this.quiz) {
          return;
        }

        this.quiz.rangeSize = value;
      }
    },

    logoImage(): string {
      if (this.newLogo) {
        return `url('${this.newLogo}')`;
      }

      const logo = this.currentLogo;

      if (!logo) {
        return "";
      }

      const cachedPreviewUrl = this.getCachedFilePreviewUrl(logo);

      if (cachedPreviewUrl) {
        return `url('${cachedPreviewUrl}')`;
      }

      return `url('${this.getServerLogoUrl(logo)}')`;
    },

    logoTypeOptions() {
      return [
        {
          text: "Только логотип",
          value: "onlyLogo",
          variant: "tab",
          active: this.isOnlyLogoStyleLogo
        },
        {
          text: "Логотип + название",
          value: "both",
          variant: "tab",
          active: this.isBothStyleLogo
        },
        {
          text: "Только название",
          value: "onlyName",
          variant: "tab",
          active: this.isOnlyNameStyleLogo
        }
      ] as SelectItem[];
    },

    logoActions(): ListActionItem[] {
      const hasLogo = Boolean(this.currentLogo || this.newLogo);

      return [
        {
          text: "Загрузить",
          icon: this.uploadIcon,
          handler: this.uploadLogoFromMenu,
          disabled: this.isOnlyNameStyleLogo,
          variant: "button"
        },
        {
          text: "Удалить",
          icon: this.deleteIcon,
          handler: this.deleteLogo,
          buttonComponent: markRaw(GhostButtonError),
          disabled: !hasLogo || this.isOnlyNameStyleLogo,
          variant: "button"
        }
      ];
    },

    isShowLogoSlider(): boolean {
      return !this.mobileVersion;
    },

    currentLogo(): string {
      const logo = this.modifyQuiz("get", "", ["logo"]) as string | undefined;

      if (typeof logo === "string" && logo) {
        return logo;
      }

      const abtest = Boolean(localStorage.getItem("ABTest"));
      const abtestobj = this.quiz?.ABTestObj as {logo?: string} | undefined;

      if (!abtest && typeof this.quiz?.logo === "string") {
        return this.quiz.logo;
      }

      if (abtest && typeof abtestobj?.logo === "string") {
        return abtestobj.logo;
      }

      return "";
    }
  },

  watch: {
    quiz: {
      handler() {
        this.setDefaultLogoType();
      },
      deep: true
    },

    mobileVersion(): void {
      this.setDefaultLogoType();
    },

    cachedFiles(): void {
      Object.values(this.cachedPreviewUrls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      this.cachedPreviewUrls = {};
    }
  }
});
</script>
